// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';
import React, { useEffect, useMemo } from 'react';
import type { Action, ThresholdResults } from 'flow/store/engine/types';
import { OperationMessageType } from 'flow/store/engine/types';
import { WorkflowOperationType } from 'flow/store/engine/constants';
import { createWorkflowOperationMessage, setConnectorCanCreateItemStatus } from 'flow/store/redux/slice';
import { selectFocusedAction, selectLegacyMappings } from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { ContainerType } from './types';
import { checkIfRuleHasAmbiguousKey } from '~/dashboard/views/workflows/service/thresholds';
import type { NodeTooltipProps } from '../../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import RuleBuilderContainer from './RuleBuilderContainer/RuleBuilderContainer';
import { reversedObjectKeyVal } from '~/entities/evaluations/TagRuleModal/utils';
import {
  selectIsRuleExplainabilityPortfolioEvaluation,
  selectThresholdOutputResults,
} from 'flow/store/redux/selectors/workflowFeatures';
import { useSourceActionThresholdResults } from './utils';

type ThresholdsTabProps = {
  action?: Action;
  containerType?: ContainerType;
  isPreviewAction?: boolean;
  thresholdResults?: ThresholdResults;
  ActionHoverTooltip?: (NodeTooltipProps: NodeTooltipProps) => JSX.Element;
};

export default function ThresholdsTab({
  action,
  containerType = ContainerType.DRAWER,
  isPreviewAction = false,
  thresholdResults = null,
  ActionHoverTooltip = null,
}: ThresholdsTabProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);
  const isREPortfolioEvaluation = useAppSelector(selectIsRuleExplainabilityPortfolioEvaluation);
  const legacyMappings = useAppSelector(selectLegacyMappings);

  const reversedActionMappings = useMemo(() => (
    reversedObjectKeyVal(legacyMappings?.actions || {})
  ), [legacyMappings?.actions]);

  const sourceAction = action || focusedAction;

  const sourceActionThresholdResults = useSourceActionThresholdResults(
    thresholdResults,
    thresholdOutputResults,
    reversedActionMappings?.[sourceAction?.publicId] ?? null,
    isREPortfolioEvaluation,
  );

  useEffect(() => {
    const ambiguousRuleOperatorUsed = checkIfRuleHasAmbiguousKey(cloneDeep(sourceAction.rules));

    dispatch(setConnectorCanCreateItemStatus(!ambiguousRuleOperatorUsed));
    if (ambiguousRuleOperatorUsed) {
      dispatch(
        createWorkflowOperationMessage(({
          object: {
            id: uuidv4(),
            publicId: sourceAction.publicId,
            type: OperationMessageType.actionRuleThreshold,
            timestamp: Date.now(),
            message: WorkflowOperationType.AMBIGUOUS_RULE_FOUND,
            data: undefined,
          },
        })),
      );
    }
  }, [dispatch, sourceAction.publicId, sourceAction.rules]);

  if (!sourceAction) return null;

  return (
    <RuleBuilderContainer
      sourceAction={sourceAction}
      containerType={containerType}
      thresholdResults={sourceActionThresholdResults}
      isPreviewOutcome={isPreviewAction}
      ActionHoverTooltip={ActionHoverTooltip}
    />
  );
}
